<div class="container">

  <header class="page-heading">
    <h1>Home Loan EMI Calculator</h1>
    <p>Calculate your home loan EMI, total interest and full amortization schedule instantly — with prepayment planning to see how much you can save.</p>
  </header>

  <!-- ══ FORM ROW ════════════════════════════════════════════════════════ -->
  <div class="top-grid mb-lg">

    <!-- ── LOAN DETAILS ──────────────────────────────────────────────── -->
    <div class="card">
      <div class="section-title">Loan Details</div>

      <!-- Loan Amount -->
      <div class="field-group" [class.has-error]="hasError('loanAmount')">
        <label class="field-label" for="loanAmount">Loan Amount</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="loanAmount" type="number"
            [(ngModel)]="loanAmount"
            [min]="C.amount.min" [max]="C.amount.max" [step]="C.amount.step"
            (ngModelChange)="onFieldChange('loanAmount')"
            (blur)="onFieldBlur('loanAmount')"
            placeholder="e.g. 1,00,00,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">{{ C.amount.min | currency:'INR':'symbol':'1.0-0':'en-IN' }} – {{ C.amount.max | currency:'INR':'symbol':'1.0-0':'en-IN' }}</div>
        @if (hasError('loanAmount')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('loanAmount') }}
          </div>
        }
      </div>

      <div class="field-row-2">
        <!-- Interest Rate -->
        <div class="field-group" [class.has-error]="hasError('annualInterestRate')">
          <label class="field-label" for="annualRate">Annual Interest Rate</label>
          <div class="input-shell suffix">
            <input id="annualRate" type="number"
              [(ngModel)]="annualInterestRate"
              [min]="C.rate.min" [max]="C.rate.max" [step]="C.rate.step"
              (ngModelChange)="onFieldChange('annualInterestRate')"
              (blur)="onFieldBlur('annualInterestRate')"
              placeholder="e.g. 8.5"
              autocomplete="off"/>
            <span class="affix right">%</span>
          </div>
          @if (hasError('annualInterestRate')) {
            <div class="field-error">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {{ fieldError('annualInterestRate') }}
            </div>
          }
        </div>

        <!-- Loan Term -->
        <div class="field-group" [class.has-error]="hasError('loanTermYears')">
          <label class="field-label" for="termYears">Loan Term</label>
          <div class="input-shell suffix">
            <input id="termYears" type="number"
              [(ngModel)]="loanTermYears"
              [min]="C.term.min" [max]="C.term.max" [step]="C.term.step"
              (ngModelChange)="onFieldChange('loanTermYears')"
              (blur)="onFieldBlur('loanTermYears')"
              placeholder="e.g. 20"
              autocomplete="off"/>
            <span class="affix right">Yr</span>
          </div>
          @if (hasError('loanTermYears')) {
            <div class="field-error">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {{ fieldError('loanTermYears') }}
            </div>
          }
        </div>
      </div>

      <!-- Start Date -->
      <div class="field-group" [class.has-error]="hasError('loanStartDate')">
        <label class="field-label" for="startDate">Loan Start Date</label>
        <input id="startDate" type="month"
          [(ngModel)]="loanStartDate"
          (ngModelChange)="onFieldChange('loanStartDate')"
          (blur)="onFieldBlur('loanStartDate')"/>
        @if (hasError('loanStartDate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('loanStartDate') }}
          </div>
        }
      </div>

      <!-- ── PREPAYMENT SECTION ──────────────────────────────────────── -->
      <div class="prepay-block">
        <div class="prepay-block-header">
          <span class="prepay-block-title">Prepayments</span>
          <div class="mode-toggle">
            <button type="button" class="mode-btn" [class.active]="prepaymentOption==='reduce_tenure'"
              (click)="prepaymentOption='reduce_tenure'">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              Reduce Tenure
            </button>
            <button type="button" class="mode-btn" [class.active]="prepaymentOption==='reduce_emi'"
              (click)="prepaymentOption='reduce_emi'">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
              Reduce EMI
            </button>
          </div>
        </div>

        <!-- Add row -->
        <div class="prepay-add-panel">
          <div class="prepay-add-fields">
            <div class="prepay-field-wrap" [class.has-error]="!!prepayError('month')">
              <label class="field-label" for="prepayMonth">At Month #</label>
              <div class="input-shell prefix">
                <span class="affix">#</span>
                <input id="prepayMonth" type="number"
                  [(ngModel)]="newPrepaymentMonth"
                  min="1" [max]="maxLoanMonths || 360" step="1"
                  (ngModelChange)="onPrepayFieldChange('month')"
                  (blur)="onPrepayFieldBlur('month')"
                  placeholder="12"
                  autocomplete="off"/>
              </div>
              @if (prepayError('month')) {
                <div class="field-error">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {{ prepayError('month') }}
                </div>
              }
            </div>

            <div class="prepay-field-wrap prepay-field-grow" [class.has-error]="!!prepayError('amount')">
              <label class="field-label" for="prepayAmount">Prepayment Amount</label>
              <div class="input-shell prefix">
                <span class="affix">₹</span>
                <input id="prepayAmount" type="number"
                  [(ngModel)]="newPrepaymentAmount"
                  [min]="C.prepayAmount.min" [max]="C.prepayAmount.max" step="10000"
                  (ngModelChange)="onPrepayFieldChange('amount')"
                  (blur)="onPrepayFieldBlur('amount')"
                  placeholder="1,00,000"
                  autocomplete="off"/>
              </div>
              @if (prepayError('amount')) {
                <div class="field-error">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {{ prepayError('amount') }}
                </div>
              }
            </div>

            <button type="button" class="btn-add-prepay" (click)="addPrepayment()"
              [disabled]="!prepayFieldsValid && (!!prepayTouched['month'] || !!prepayTouched['amount'])">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add
            </button>
          </div>
        </div>

        <!-- Prepayment list -->
        <div class="prepay-list-wrap">
          @if (prepayments.length === 0) {
            <div class="prepay-empty">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              No prepayments added. Prepayments can significantly reduce your interest outgo.
            </div>
          } @else {
            <div class="prepay-items">
              @for (p of prepayments; track p.id) {
                <div class="prepay-item">
                  <div class="prepay-item-left">
                    <span class="pi-month-badge">Mo {{ p.month }}</span>
                    <span class="pi-arrow">→</span>
                    <span class="pi-amount">{{ fmt.currency(p.amount) }}</span>
                  </div>
                  <button type="button" class="pi-remove" (click)="removePrepayment(p.id)" title="Remove prepayment">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- Action buttons -->
      <div class="action-row">
        <button type="button" class="btn btn-primary"
          (click)="calculate()"
          [disabled]="calculating"
          [class.btn-disabled]="showFormErrors && !isFormValid">
          @if (calculating) {
            <span class="spinner"></span> Calculating…
          } @else {
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            Calculate
          }
        </button>

        @if (calculated) {
          <button type="button" class="btn btn-secondary" (click)="reset()">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
            Reset
          </button>
        }

        @if (showFormErrors && !isFormValid) {
          <span class="form-error-hint">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Fix the errors above to calculate
          </span>
        }
        @if (calcError) {
          <span class="form-error-hint">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Something went wrong calculating your results. Please try again.
          </span>
        }
      </div>
    </div>


    <!-- ── AI ADVISOR ─────────────────────────────────────────────────── -->
    <div class="card ai-card">
      <div class="ai-header">
        <div class="ai-icon-wrap">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
        </div>
        <div class="ai-header-text">
          <span class="ai-title">AI Advisor</span>
          <span class="ai-badge">Smart Suggestions</span>
        </div>
        <button type="button" class="toggle-btn" (click)="showAiAdvisor = !showAiAdvisor">
          {{ showAiAdvisor ? 'Hide' : 'Show' }}
        </button>
      </div>

      <div class="ai-body" [class.open]="showAiAdvisor">
        <p class="ai-intro">Get personalised insights. Enter your details and click Calculate.</p>

        <div class="field-row-2">
          <!-- Age -->
          <div class="field-group" [class.has-error]="hasError('borrowerAge')">
            <label class="field-label" for="borrowerAge">Your Age</label>
            <div class="input-shell suffix">
              <input id="borrowerAge" type="number"
                [(ngModel)]="borrowerAge"
                [min]="C.age.min" [max]="C.age.max" step="1"
                (ngModelChange)="onFieldChange('borrowerAge')"
                (blur)="onFieldBlur('borrowerAge')"
                placeholder="e.g. 30"
                autocomplete="off"/>
              <span class="affix right">Yr</span>
            </div>
            @if (hasError('borrowerAge')) {
              <div class="field-error">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {{ fieldError('borrowerAge') }}
              </div>
            }
          </div>

          <!-- Income -->
          <div class="field-group" [class.has-error]="hasError('monthlyIncome')">
            <label class="field-label" for="monthlyIncome">
              Monthly Income
              <span class="optional-tag">optional</span>
            </label>
            <div class="input-shell prefix">
              <span class="affix">₹</span>
              <input id="monthlyIncome" type="number"
                [(ngModel)]="monthlyIncome"
                min="0" step="5000"
                (ngModelChange)="onFieldChange('monthlyIncome')"
                (blur)="onFieldBlur('monthlyIncome')"
                placeholder="e.g. 1,00,000"
                autocomplete="off"/>
            </div>
            @if (hasError('monthlyIncome')) {
              <div class="field-error">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {{ fieldError('monthlyIncome') }}
              </div>
            }
          </div>
        </div>

        <!-- Insights -->
        <div class="insights-wrap">
          @if (!calculated) {
            <div class="insight-placeholder">
              <div class="insight-placeholder-icon"></div>
              <div>Click <strong>Calculate</strong> to receive personalised AI insights based on your loan profile.</div>
            </div>
          } @else if (aiInsights.length === 0) {
            <div class="insight-allgood">
              <span></span>
              <span>All Good! Your loan profile looks healthy.</span>
            </div>
          } @else {
            <div class="insight-scroll">
              @for (ins of aiInsights; track ins.title) {
                <div class="insight-card" [ngClass]="'insight-' + ins.type">
                  <div class="insight-row1">
                    <span class="insight-icon" [innerHTML]="ins.icon"></span>
                    <span class="insight-title">{{ ins.title }}</span>
                  </div>
                  <p class="insight-msg">{{ ins.message }}</p>
                  @if (ins.actionLabel) {
                    <button type="button" class="insight-action-btn" (click)="applyInsightAction(ins)">
                      {{ ins.actionLabel }}
                    </button>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- ══ RESULTS ══════════════════════════════════════════════════════════ -->
  @if (calculated) {

    <!-- ── SUMMARY CARDS ────────────────────────────────────────────── -->
    <div class="summary-grid mb-md">
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">Monthly EMI</div>
        <div class="sc-value">{{ fmt.currency(baseSummary!.emi) }}</div>
        @if (hasPrepayments && prepaymentOption === 'reduce_emi' && monthsSaved === 0) {
          <div class="sc-sub">New EMI: {{ fmt.currency(finalSummary!.emi) }}</div>
        }
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Principal Amount</div>
        <div class="sc-value">{{ fmt.currency(loanAmount) }}</div>
        <div class="sc-sub">{{ loanTermYears }}yr @ {{ annualInterestRate }}% p.a.</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Total Interest</div>
        <div class="sc-value">{{ fmt.currency(finalSummary!.totalInterest) }}</div>
        @if (hasPrepayments && interestSaved > 0) {
          <div class="sc-sub sc-save">↓ Saving {{ fmt.currency(interestSaved) }}</div>
        }
      </div>
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">Total Payment</div>
        <div class="sc-value">{{ fmt.currency(finalSummary!.totalPayment) }}</div>
        <div class="sc-sub">
          @if (monthsSaved > 0) { {{ monthsSaved }} months saved }
          @else { {{ Math.floor(finalSummary!.totalMonths / 12) }}y {{ finalSummary!.totalMonths % 12 }}m tenure }
        </div>
      </div>
    </div>

    <!-- ── SAVINGS BANNER ───────────────────────────────────────────── -->
    @if (hasPrepayments && interestSaved > 0) {
      <div class="savings-banner mb-md">
        <div class="sb-item">
          <div class="sb-val">{{ fmt.currency(interestSaved) }}</div>
          <div class="sb-lbl">Interest Saved</div>
        </div>
        <div class="sb-divider"></div>
        <div class="sb-item">
          <div class="sb-val">{{ monthsSaved }} months</div>
          <div class="sb-lbl">Time Saved</div>
        </div>
        <div class="sb-divider"></div>
        <div class="sb-item">
          <div class="sb-val">{{ Math.floor(finalSummary!.totalMonths/12) }}y {{ finalSummary!.totalMonths%12 }}m</div>
          <div class="sb-lbl">New Payoff</div>
        </div>
      </div>
    }

    <!-- ── CHARTS ────────────────────────────────────────────────────── -->
    <div class="card charts-card mb-md">
      <div class="section-title">Visual Breakdown</div>

      <div class="charts-top-row">
        <!-- Doughnut -->
        <div class="chart-panel">
          <div class="chart-panel-label">Principal vs Interest</div>
          <div class="chart-canvas-wrap chart-canvas-pie">
            <canvas #pieChartRef></canvas>
          </div>
        </div>

        <!-- Stacked Bar -->
        <div class="chart-panel chart-panel-bar">
          <div class="chart-panel-label">Yearly Payment Breakdown</div>
          <div class="chart-canvas-wrap">
            <canvas #barChartRef></canvas>
          </div>
        </div>
      </div>

      <!-- Balance over time — full width -->
      <div class="chart-panel mt-sm">
        <div class="chart-panel-label">Balance Over Time</div>
        <div class="chart-canvas-wrap chart-canvas-line">
          <canvas #lineChartRef></canvas>
        </div>
      </div>
    </div>

    <!-- ── AMORTIZATION TABLE ─────────────────────────────────────── -->
    <div class="card mb-md">
      <div class="section-title">Amortization Schedule</div>

      <div class="tab-bar">
        <button type="button" class="tab-btn" [class.active]="activeTab==='yearly'" (click)="activeTab='yearly'">
          Yearly Summary
        </button>
        <button type="button" class="tab-btn" [class.active]="activeTab==='monthly'" (click)="activeTab='monthly'">
          Full Monthly Schedule
        </button>
        <div class="tab-count">
          @if (activeTab === 'yearly') { {{ yearlySchedule.length }} years }
          @else { {{ monthlySchedule.length }} months }
        </div>
      </div>

      <!-- Yearly -->
      @if (activeTab === 'yearly') {
        <div class="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th class="t-left">Year</th>
                <th>EMI Paid</th>
                <th class="t-principal">Principal</th>
                <th class="t-interest">Interest</th>
                @if (hasPrepayments) { <th class="t-prepay">Prepayment</th> }
                <th>Total Paid</th>
                <th class="t-balance">Balance</th>
                <th class="t-expand"></th>
              </tr>
            </thead>
            <tbody>
              @for (yr of yearlySchedule; track yr.calendarYear) {
                <tr class="yr-row" (click)="toggleYear(yr.calendarYear)">
                  <td class="t-left t-year">{{ yr.calendarYear }}</td>
                  <td>{{ fmt.currency(yr.emi) }}</td>
                  <td class="t-principal">{{ fmt.currency(yr.principal) }}</td>
                  <td class="t-interest">{{ fmt.currency(yr.interest) }}</td>
                  @if (hasPrepayments) {
                    <td class="t-prepay">{{ yr.prepayment > 0 ? fmt.currency(yr.prepayment) : '—' }}</td>
                  }
                  <td>{{ fmt.currency(yr.emi + yr.prepayment) }}</td>
                  <td class="t-balance">{{ fmt.currency(yr.balance) }}</td>
                  <td class="t-expand">
                    <span class="expand-chevron" [class.open]="expandedYear === yr.calendarYear">›</span>
                  </td>
                </tr>
                @if (expandedYear === yr.calendarYear) {
                  <tr class="inner-row">
                    <td [attr.colspan]="hasPrepayments ? 8 : 7" style="padding:0">
                      <table class="inner-tbl">
                        <thead>
                          <tr>
                            <th class="t-left">Month</th>
                            <th>EMI</th>
                            <th class="t-principal">Principal</th>
                            <th class="t-interest">Interest</th>
                            @if (hasPrepayments) { <th class="t-prepay">Prepayment</th> }
                            <th class="t-balance">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (m of getMonthsForYear(yr.calendarYear); track m.month) {
                            <tr [class.prepay-highlight]="m.prepayment > 0">
                              <td class="t-left">{{ getMonthName(m.calendarMonth) }} {{ m.calendarYear }}</td>
                              <td>{{ fmt.currency(m.emi) }}</td>
                              <td class="t-principal">{{ fmt.currency(m.principal) }}</td>
                              <td class="t-interest">{{ fmt.currency(m.interest) }}</td>
                              @if (hasPrepayments) {
                                <td class="t-prepay">{{ m.prepayment > 0 ? fmt.currency(m.prepayment) : '—' }}</td>
                              }
                              <td class="t-balance">{{ fmt.currency(m.balance) }}</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      }

      <!-- Monthly -->
      @if (activeTab === 'monthly') {
        <div class="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th class="t-left">#</th>
                <th class="t-left">Month</th>
                <th>EMI</th>
                <th class="t-principal">Principal</th>
                <th class="t-interest">Interest</th>
                @if (hasPrepayments) { <th class="t-prepay">Prepayment</th> }
                <th class="t-balance">Balance</th>
              </tr>
            </thead>
            <tbody>
              @for (m of monthlySchedule; track m.month) {
                <tr [class.prepay-highlight]="m.prepayment > 0">
                  <td class="t-left t-num">{{ m.month }}</td>
                  <td class="t-left">{{ getMonthName(m.calendarMonth) }} {{ m.calendarYear }}</td>
                  <td>{{ fmt.currency(m.emi) }}</td>
                  <td class="t-principal">{{ fmt.currency(m.principal) }}</td>
                  <td class="t-interest">{{ fmt.currency(m.interest) }}</td>
                  @if (hasPrepayments) {
                    <td class="t-prepay">{{ m.prepayment > 0 ? fmt.currency(m.prepayment) : '—' }}</td>
                  }
                  <td class="t-balance">{{ fmt.currency(m.balance) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>

  } <!-- /if calculated -->
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is Home Loan EMI Calculated?</h2>
    <p>
      Your home loan EMI (Equated Monthly Installment) is the fixed amount you pay every month
      towards principal and interest, calculated using the reducing-balance method followed by
      Indian banks and NBFCs.
    </p>
    <p class="emi-formula">EMI = [P &times; R &times; (1 + R)<sup>N</sup>] &divide; [(1 + R)<sup>N</sup> &minus; 1]</p>
    <ul class="emi-formula-vars">
      <li><strong>P</strong> &mdash; Principal loan amount</li>
      <li><strong>R</strong> &mdash; Monthly interest rate (annual interest rate &divide; 12 &divide; 100)</li>
      <li><strong>N</strong> &mdash; Loan tenure in months</li>
    </ul>
    <p>
      For example, a &#8377;50,00,000 home loan at 8.5% annual interest for 20 years works out to
      an EMI of about &#8377;43,391 per month. Use the calculator above to compute your exact EMI,
      view the month-by-month amortization schedule, and see how prepayments reduce your total
      interest outgo.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>What is EMI in a home loan?</h3>
      <p>
        EMI stands for Equated Monthly Installment &mdash; a fixed monthly payment that combines
        both principal repayment and interest, calculated so that the loan is fully repaid by the
        end of its tenure.
      </p>
    </div>
    <div class="faq-item">
      <h3>How does prepayment reduce my home loan interest?</h3>
      <p>
        Every prepayment is applied directly to your outstanding principal, which lowers the
        interest charged in every subsequent month. You can choose to use the savings to either
        reduce your remaining tenure or lower your EMI amount. To model a lump-sum or extra monthly
        payment on its own, try our dedicated
        <a routerLink="/home-loan-prepayment">Home Loan Prepayment Calculator</a>.
      </p>
    </div>
    <div class="faq-item">
      <h3>Does the interest rate or the loan tenure affect EMI more?</h3>
      <p>
        Both affect EMI, but tenure has a bigger long-term cost impact: a longer tenure lowers the
        EMI but sharply increases total interest paid, while a small change in interest rate has a
        smaller effect on the monthly EMI but still adds up over a long tenure.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/how-much-home-loan-can-i-afford">How much home loan can I actually afford?</a>
    </p>
  </div>
</section>
